import xlsx from 'xlsx-style'
import { isNumber, keys, range } from 'lodash'
import { mapToObject } from '../lib/Tools'

/**
 * Based on http://sheetjs.com/demos/writexlsx.html
 */

/**
 * Aux functions for xlsx-js
 */
function datenum(v, date1904) {
  if (date1904) 
    v += 1462;
  const epoch = Date.parse(v);
  return (epoch - new Date(Date.UTC(1899, 11, 30))) / (24 * 60 * 60 * 1000);
}
function sheetFromMatrix(data) {
  const ws = {};
  const range = {
    s: {
      c: 10000000,
      r: 10000000
    },
    e: {
      c: 0,
      r: 0
    }
  };
  for (var R = 0; R != data.length; ++R) {
    for (var C = 0; C != data[R].length; ++C) {
      if (range.s.r > R) range.s.r = R;
      if (range.s.c > C) range.s.c = C;
      if (range.e.r < R) range.e.r = R;
      if (range.e.c < C) range.e.c = C;
      var cell = {
        v: data[R][C] || (isNumber(data[R][C]) ? 0 : "")
      };
      if (cell.v == null) continue;
      var cell_ref = xlsx.utils.encode_cell({
        c: C,
        r: R
      });

      if (typeof cell.v === 'number') cell.t = 'n';
      else if (typeof cell.v === 'boolean') cell.t = 'b';
      else if (cell.v instanceof Date) {
        cell.t = 'n';
        cell.z = xlsx.SSF._table[14];
        cell.v = datenum(cell.v);
      } 
      else cell.t = 's';

      ws[cell_ref] = cell;
    }
  }
  if (range.s.c < 10000000) ws['!ref'] = xlsx.utils.encode_range(range);
  return ws;
}

function Workbook() {
  if (!(this instanceof Workbook)) return new Workbook();
  this.SheetNames = [];
  this.Sheets = {};
}

function s2ab(s) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i != s.length; ++i) 
    view[i] = (s.charCodeAt(i) & 0xFF);
  return buf;
}

/**
 * Genera un blob con un excel de una sóla página
 */
// WARNING: can't style cells past the Z column
const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")
const l2n = l => letters.indexOf(l) + 1
const n2l = n => letters[n - 1]
const _splitRange = (start, end) => {
  const startX = l2n(start[0])
  const endX = l2n(end[0])
  const startY = parseInt(start.slice(1))
  const endY = parseInt(end.slice(1))

  const results = []

  for (let i = startX; i <= endX; ++i)
    for (let j = startY; j <= endY; ++j) 
      results.push(`${n2l(i)}${j}`)

  return results
}
const splitRange = r => {
  const rs = r.split("-")
  if (rs.length === 1) return [r]
  if (rs.length !== 2) { 
    console.error(`incorrect cell range: ${r}`); 
    return [];
  }

  return _splitRange(rs[0], rs[1])
}

/**
 * Estilo para el Excel de Contrato
 */
const someColors = [
  'FFDFDEFE',
  'FF39B5B5',
  'FFFAD54B',
  'FF83E4FE',
  'FFF88E5C',
  'FFB9CF63'
]
const getColor = i => someColors[i % someColors.length]

export const contratoExcelStyle = (numRows: number, rowsForCities: number[][], includedLastLine: boolean) => {

  const _cityRows = rowsForCities.map(a => a.map(i => i + 8))
  const cityColors = {} 
  rowsForCities.forEach((cityRows, i) => {
    const color = getColor(i)
    cityRows.forEach(j => {
      cityColors[`A${j+8}-D${j+8}`] = { fill: { fgColor: {rgb:color}, patternType: "solid"}}
    })
  })

  const zebraEffect = {}
  range(3, numRows+30, 2).forEach(n => { 
    zebraEffect[`A${n}-D${n}`] = {fill: {fgColor: {rgb:"FFEAEAEA"}, patternType: "solid"}} 
  })
  
  return {
    cols: [
      {wpx:178},
      {wpx:240},
      {wpx:136},
      {wpx:150},
      {wch:50},
      {wch:50},
      {wch:50},
      {wch:50},
      {wch:50},
    ],
    cells: {
      // fontface and borders
      "A1-G200": {
        font: { name: "Avenir Next" }, 
        border: {
          top:    { style: "thin", color: {rgb: "00FFFFFF"}}, 
          bottom: { style: "thin", color: {rgb: "00FFFFFF"}}, 
          left:   { style: "thin", color: {rgb: "00FFFFFF"}}, 
          right:  { style: "thin", color: {rgb: "00FFFFFF"}}, 
        }
      }, 
      // first column, all centered 
      "A1-A200": {
        alignment: { horizontal: "center" }
      }, 
      // zebra effect
      ...zebraEffect,
      // color per city
      ...cityColors,
      // title
      B1: {
        font: {
          sz: "18"
        },
        alignment: {
          horizontal: "center"
        }
      },
      // column titles
      "A7-D7": {
        font: {
          sz: "18"
        },
        alignment: {
          horizontal: "center"
        },
        fill: {
          patternType: "solid",
          fgColor: {
            rgb: "FF819499"
          }
        }
      },
      // trip data
      "A3-B6": {
        alignment: {
          horizontal: "center"
        }
      }, 
      // total price (last row)
      [`B${numRows - 31 - (includedLastLine ? 1 : 0)}`]: {
        font: { sz: "16" }
      }
    }
  }
}

export const planDeViajeStyle = (csv) => {

  const dayStartRows = csv
    .map((row, i) => (row[0].match(/^Day .*$/) ? i + 1 : null))
    .filter(isNumber)
  
  // the first column, with the dates, is painted blue
  const dayRanges = dayStartRows.map((start, i) => {
    // the day ends one cell before the next one starts
    // the last day ends where data ends
    const end = (i == (dayStartRows.length-1) ? csv.length - 1 : (dayStartRows[i+1] - 1))
    return { start, end }
  })

  const finalBorder = mapToObject(
    dayRanges.map(range => `F${range.start}-F${range.end}`), 
    () => ({
      border: { 
        right: { style: "medium", color: {rgb: "#000000"}}
      }, 
    })
  )

  const blueDays = mapToObject(
    dayRanges.map(range => `A${range.start}-A${range.end}`), 
    () => ({
      border: { 
        bottom: { style: "thin", color: {rgb: "FF819499"}},
        right: { style: "medium", color: {rgb: "#000000"}}
      }, 
      fill: {
        patternType: "solid",
        fgColor: { rgb: "FF819499" }, 
      }
    })
  )

  const dayBorders = mapToObject(
    dayRanges.map(range => `A${range.end}-F${range.end}`), 
    () => ({
      border: { bottom: { style: "medium", color: {rgb: "#000000"}}}, 
    })
  )
  
  return {
    cols: [
      {wpx:80},
      {wpx:100},
      {wpx:340},
      {wpx:260},
      {wpx:200},
      {wpx:250},
    ],
    cells: {
      // fontface and borders
      "A3-G2000": {
        font: { name: "Avenir Next", sz: "12" }, 
        border: {
          top:    { style: "none", color: {rgb: "FFFFFFFF"}}, 
          bottom: { style: "none", color: {rgb: "FFFFFFFF"}}, 
          left:   { style: "none", color: {rgb: "FFFFFFFF"}}, 
          right:  { style: "none", color: {rgb: "FFFFFFFF"}}, 
        }
      }, 
      "A3-F2000": { 
        border: {  
          right: { style: "medium", color: { rgb: "FFCCCCCC" }} 
        } 
      },
      ...blueDays,
      ...dayBorders,
      ...finalBorder,
      // title
      D1: {
        font: {
          sz: "18"
        },
        alignment: {
          horizontal: "center"
        }
      },
      // column titles
      "A2-F2": {
        border: { 
          right: { style: "medium", color: {rgb: "#000000"}},
          top: { style: "medium", color: {rgb: "#000000"}},
          bottom: { style: "medium", color: {rgb: "#000000"}}
        }, 
        font: {
          sz: "18"
        },
        alignment: {
          horizontal: "center"
        },
        fill: {
          patternType: "solid",
          fgColor: {
            rgb: "FF819499"
          }
        }
      },

    }
  }
}

/**
 * Combina dos objetos de estilo que aplican sobre la misma celda
 */
const extendTwoLevels = (base, added) => {
  if (!added && !base) return {}
  if (!added) return base
  if (!base) return added
  const x = {}; 
  keys(base).forEach(k => {
    if (added[k]) x[k] = { ...base[k], ...added[k] } ;
    else x[k] = base[k];
  }); 
  keys(added).filter(k => !x[k]).forEach(k => { x[k] = added[k] });
  return x;
}

/**
 * Crea un fichero de excel en un blob
 */
export function createExcelBlob(data: Array<Array<boolean|string|Date|number>>, style: ?Object) {
  const sheet = sheetFromMatrix(data)
  const sheetName = "Sheet 1"

  if (style) {
    if (style.cells) {
      keys(style.cells).forEach(range => { 
        splitRange(range).forEach(cell => { 
          if (sheet[cell]) {
            const sc = sheet[cell]
            sc.s = extendTwoLevels(sc.s, style.cells[range] )
            // console.log(`style for cell ${cell} is ${JSON.stringify(sheet[cell].s)}`)
          }
        })
      })
    }
    if (style.cols) {
      sheet['!cols'] = style.cols
    }
  }


  const wb = new Workbook()
  wb.SheetNames.push(sheetName)
  wb.Sheets[sheetName] = sheet

  const wbout = xlsx.write(wb, {bookType: "xlsx", bookSST: true, type: "binary"})

  return new Blob([s2ab(wbout)], { type: "application/octet-stream" })
}

export function createReportExcelBlob(
  sellsData: Array<Array<boolean|string|Date|number>>, 
  purchasesData: Array<Array<boolean|string|Date|number>>, 
  sellsStyle: ?Object, 
  purchasesStyle: ?Object
) 
{
  const sellsSheet = sheetFromMatrix(sellsData)
  const purchasesSheet = sheetFromMatrix(purchasesData)
  const sellsSheetName = "Ventas"
  const purchasesSheetName = "Compras"

  if (sellsStyle) {
    if (sellsStyle.cells) {
      keys(sellsStyle.cells).forEach(range => { 
        splitRange(range).forEach(cell => { 
          if (sellsSheet[cell]) {
            const sc = sellsSheet[cell]
            sc.s = extendTwoLevels(sc.s, sellsStyle.cells[range])
            // console.log(`style for cell ${cell} is ${JSON.stringify(sheet[cell].s)}`)
          }
        })
      })
    }
    if (sellsStyle.cols) {
      sellsSheet['!cols'] = sellsStyle.cols
    }
  }

  if (purchasesStyle) {
    if (purchasesStyle.cells) {
      keys(purchasesStyle.cells).forEach(range => { 
        splitRange(range).forEach(cell => { 
          if (purchasesSheet[cell]) {
            const sc = purchasesSheet[cell]
            sc.s = extendTwoLevels(sc.s, purchasesStyle.cells[range] )
            // console.log(`style for cell ${cell} is ${JSON.stringify(sheet[cell].s)}`)
          }
        })
      })
    }
    if (purchasesStyle.cols) {
      purchasesSheet['!cols'] = purchasesStyle.cols
    }
  }

  const wb = new Workbook()
  wb.SheetNames.push(sellsSheetName)
  wb.Sheets[sellsSheetName] = sellsSheet
  wb.SheetNames.push(purchasesSheetName)
  wb.Sheets[purchasesSheetName] = purchasesSheet

  const wbout = xlsx.write(wb, {bookType: "xlsx", bookSST: true, type: "binary"})

  return new Blob([s2ab(wbout)], { type: "application/octet-stream" })
}
